import cn from "classnames";
import { alphabeticalSort } from "helpers/system";
import debounce from "lodash/debounce";
import React, { useCallback, useMemo, useState } from "react";
import { CustomScroll } from "react-custom-scroll";
import { connect } from "react-redux";
import AcceptPage from "./acceptPage";
import data from "./data";
import EmptyPage from "./emptyPage";

const ROW_HEIGHT = 64;
const directionMapper = { asc: "desc", desc: "asc" };
const carsCountIndex = { "1 - 50": 0, "50 - 100": 1, "100+": 2 };

const OrganizationRatings = (props) => {
    const hasOrganization = data.some((row) => row[0] === props.profile.organizationId);

    const [acceptProcessData, setAcceptProcessData] = useState(false);

    const [rows, setRows] = useState(data);
    const [sorting, setSorting] = useState({ column: 4, direction: "desc" });
    const [filters, setFilters] = useState({ 1: "", 2: "", 3: { from: "", to: "" }, 4: { from: "", to: "" } });

    const filterItems = useCallback(
        debounce((newFilters) => {
            let newRows = [...rows];
            [1, 2].forEach((index) => {
                if (newFilters[index]) {
                    newRows = newRows.filter((row) => row[index] === newFilters[index]);
                }
            });

            [3, 4].forEach((index) => {
                if (newFilters[index].from || newFilters[index].to) {
                    newRows = newRows.filter((row) => {
                        if (newFilters[index].from && newFilters[index].to) {
                            return row[index] >= newFilters[index].from && row[index] <= newFilters[index].to;
                        } else if (newFilters[index].from) {
                            return row[index] >= newFilters[index].from;
                        } else {
                            return row[index] <= newFilters[index].to;
                        }
                    });
                }
            });

            setRows(newRows);
        }, 500),
        [],
    );

    const scrollHeight = useMemo(() => {
        let rowPosition = rows.findIndex((row) => row[0] === props.profile.organizationId) * ROW_HEIGHT;
        rowPosition = rowPosition > 3 * ROW_HEIGHT ? rowPosition - 2 * ROW_HEIGHT : rowPosition;
        return rowPosition;
    }, [props.profile.organizationId, rows]);

    const changeSorting = (name) => {
        let newSorting;
        if (name === sorting.column) {
            newSorting = { column: name, direction: directionMapper[sorting.direction] };
        } else {
            newSorting = { column: name, direction: "asc" };
        }
        setSorting(newSorting);

        setRows(
            [...rows].sort((a, b) => {
                if (newSorting.column === 2) {
                    return newSorting.direction === "asc"
                        ? carsCountIndex[a[newSorting.column]] - carsCountIndex[b[newSorting.column]]
                        : carsCountIndex[b[newSorting.column]] - carsCountIndex[a[newSorting.column]];
                }
                if (newSorting.column > 2) {
                    return newSorting.direction === "asc"
                        ? a[newSorting.column] - b[newSorting.column]
                        : b[newSorting.column] - a[newSorting.column];
                } else {
                    return newSorting.direction === "asc"
                        ? alphabeticalSort(a, b, newSorting.column)
                        : -alphabeticalSort(a, b, newSorting.column);
                }
            }),
        );
    };

    const columnSortIcon = (column) => {
        if (sorting.column !== column) {
            return <i className="icon-uEA68-sort-solid" />;
        }
        return sorting.direction === "asc" ? (
            <i className="icon-uEAAC-sort-down-solid" />
        ) : (
            <i className="icon-uEAAD-sort-up-solid" />
        );
    };

    const onFilterChange = (value, index, key) => {
        const newFilters = { ...filters };
        if (key) {
            newFilters[index][key] = value;
        } else {
            newFilters[index] = value;
        }
        setFilters(newFilters);

        filterItems(newFilters);
    };

    const carTypes = useMemo(() => {
        const types = new Set();
        data.forEach((item) => {
            types.add(item[1]);
        });
        return [...types];
    }, []);

    if (!hasOrganization) {
        return <EmptyPage />;
    }

    return (
        <div className="results__wrap">
            {!acceptProcessData && <AcceptPage setAcceptProcessData={setAcceptProcessData} />}
            <div className="results__header">
                <div className="results-header__logo" />
                <div className="results-header__title">Таблица лидеров</div>
            </div>
            <div className="results__main">
                <CustomScroll heightRelativeToParent="100%" scrollTo={scrollHeight}>
                    <div className="table-wrap">
                        <table className="results-table">
                            <thead>
                                <tr>
                                    <th />
                                    <th className="cursor-pointer">
                                        <div onClick={changeSorting.bind(undefined, 1)}>
                                            Парк
                                            <small className="ml-2">{columnSortIcon(1)}</small>
                                        </div>
                                        <div className="d-flex results__filter-wrap">
                                            <select
                                                className="results__filter-input results__filter-input--wide"
                                                value={filters[1]}
                                                onChange={(e) => onFilterChange(e.target.value, 1)}
                                            >
                                                <option value="">Все</option>
                                                {carTypes.map((type) => (
                                                    <option key={type} value={type}>
                                                        {type}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </th>
                                    <th className="cursor-pointer">
                                        <div onClick={changeSorting.bind(undefined, 2)}>
                                            Количество машин
                                            <small className="ml-2">{columnSortIcon(2)}</small>
                                        </div>
                                        <div className="d-flex results__filter-wrap">
                                            <select
                                                className="results__filter-input results__filter-input--wide"
                                                value={filters[2]}
                                                onChange={(e) => onFilterChange(e.target.value, 2)}
                                            >
                                                <option value="">Все</option>
                                                <option value="1 - 50">1 - 50</option>
                                                <option value="50 - 100">50 - 100</option>
                                                <option value="100+">100+</option>
                                            </select>
                                        </div>
                                    </th>
                                    <th className="cursor-pointer">
                                        <div onClick={changeSorting.bind(undefined, 3)}>
                                            Рубль/км
                                            <small className="ml-2">{columnSortIcon(3)}</small>
                                        </div>
                                        <div className="d-flex results__filter-wrap">
                                            <input
                                                className="results__filter-input"
                                                placeholder="От"
                                                value={filters[3].from}
                                                onChange={(e) => onFilterChange(e.target.value, 3, "from")}
                                            />
                                            <span className="mx-2">&mdash;</span>
                                            <input
                                                className="results__filter-input"
                                                placeholder="До"
                                                value={filters[3].to}
                                                onChange={(e) => onFilterChange(e.target.value, 3, "to")}
                                            />
                                        </div>
                                    </th>
                                    <th className="cursor-pointer">
                                        <div onClick={changeSorting.bind(undefined, 4)}>
                                            Рубль/час
                                            <small className="ml-2">{columnSortIcon(4)}</small>
                                        </div>
                                        <div className="d-flex results__filter-wrap">
                                            <input
                                                className="results__filter-input"
                                                placeholder="От"
                                                value={filters[4].from}
                                                onChange={(e) => onFilterChange(e.target.value, 4, "from")}
                                            />
                                            <span className="mx-2">&mdash;</span>
                                            <input
                                                className="results__filter-input"
                                                placeholder="До"
                                                value={filters[4].to}
                                                onChange={(e) => onFilterChange(e.target.value, 4, "to")}
                                            />
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((item, i) => (
                                    <tr
                                        key={i}
                                        className={cn("results-table__row", {
                                            "results-table__row--current": item[0] === props.profile.organizationId,
                                        })}
                                    >
                                        <td>
                                            <div className="results-table__medal" />
                                            <div className="position-relative">{i + 1}</div>
                                        </td>
                                        <td>{item[1]}</td>
                                        <td>{item[2]}</td>
                                        <td>{item[3]}</td>
                                        <td>{item[4]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CustomScroll>
            </div>
            <div className="results__footer">
                <div className="results-footer__logo--right" />
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({ profile: state.profile.profile });

export default connect(mapStateToProps, () => ({}))(OrganizationRatings);
